import React, { useState, useMemo, useEffect } from 'react';
import styles from './playlist.module.scss';
import { PlaylistItem as VideoConfigPlaylistItem } from './videoConfig';
import { useTranslation } from 'react-i18next';
import BtButton from '../bt-button/bt-button';
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/solid';

export interface PlaylistProps {
  title: string;
  description?: string;
  items: VideoConfigPlaylistItem[];
  className?: string;
}

const Playlist: React.FC<PlaylistProps> = ({ 
  title, 
  description, 
  items, 
  className = '' 
}) => {
  const [t] = useTranslation('help');
  const [selectedItem, setSelectedItem] = useState<VideoConfigPlaylistItem | null>(items[0] || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isNarrowScreen, setIsNarrowScreen] = useState(window.innerWidth < 1700);
  const [isPlaylistItemsOpen, setIsPlaylistItemsOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsNarrowScreen(window.innerWidth < 1700);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    
    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      t(item.title, item.title).toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query)
    );
  }, [items, searchQuery, t]);

  const handleItemSelect = (item: VideoConfigPlaylistItem) => {
    setSelectedItem(item);
  };

  const handlePlaylistItemsToggle = () => {
    setIsPlaylistItemsOpen(!isPlaylistItemsOpen);
  };

  return (
    <div className={`${styles.playlist} ${className}`}>
      <div className={styles.playlistHeader}>
        {description && (
          <p className={styles.playlistDescription}>{description}</p>
        )}
      </div>

      <div className={styles.playlistContent}>
        <div className={styles.videoContainer}>
          {selectedItem ? (
            <iframe 
              src={selectedItem.src}
              width="990px"
              height="600px"
              frameBorder="0"
              allowFullScreen
              title={t(selectedItem.title, selectedItem.title)}
            />
          ) : (
            <div className={styles.noVideoSelected}>
              <p>{t('playlist.selectGuide', 'Select a guide from the playlist to start watching')}</p>
            </div>
          )}
        </div>

        {/* Playlist Items */}
        <div className={
          isNarrowScreen
            ? `${styles.playlistItemsSmall} ${isPlaylistItemsOpen ? styles.playlistItemsSmallOpen : styles.playlistItemsSmallClosed}`
            : styles.playlistItems
        }>
          <div className={styles.playlistItemsHeader}>
            <h4 className={styles.playlistItemsTitle}>{t('playlist.title', 'Playlist')} ({filteredItems.length} {t('playlist.guides', 'guides')})</h4>
            {items.length > 10 && (
              <div className={styles.searchContainer}>
                <input
                  type="text"
                  placeholder={t('playlist.searchPlaceholder', 'Sök i spellistan...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                /> 
              </div>
            )}
            {isNarrowScreen && (
              <BtButton color='gray' size='xxs' class={styles.playlistItemsToggleButton} onClick={handlePlaylistItemsToggle}>{isPlaylistItemsOpen ? <ChevronDoubleRightIcon /> : <ChevronDoubleLeftIcon />}</BtButton>
            )}
          </div>
          <div className={styles.playlistList}>
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <div
                  key={item.id}
                  className={`${styles.playlistItem} ${
                    selectedItem?.id === item.id ? styles.playlistItemActive : ''
                  }`}
                  onClick={() => handleItemSelect(item)}
                >
                  <div className={styles.playlistItemNumber}>
                    {items.findIndex(originalItem => originalItem.id === item.id) + 1}
                  </div>
                  <div className={styles.playlistItemContent}>
                    <h5 className={styles.playlistItemTitle}>{t(item.title, item.title)}</h5>
                    {item.description && (
                      <p className={styles.playlistItemDescription}>
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.noResults}>
                <p>{t('playlist.noGuidesFound', 'Inga guider hittades för "{{query}}"', { query: searchQuery })}</p>
              </div>
            )}
          </div>
        </div>
        {isNarrowScreen && (
          <BtButton
            color='gray'
            size='xxs'
            class={`${styles.playlistItemsToggleButtonFloat} ${!isPlaylistItemsOpen ? styles.playlistItemsToggleButtonFloatVisible : styles.playlistItemsToggleButtonFloatHidden}`}
            onClick={handlePlaylistItemsToggle}
          >
            <ChevronDoubleLeftIcon />
          </BtButton>
        )}
      </div>
    </div>
  );
};

export default Playlist;
